<template>
  <CCardBody>
    <CDataTable
      class="custom-pagination"
      :items="ticketsData"
      :fields="tableFields"
      :table-filter="{ external: true, placeholder: $t('table.user.placeholderSearch') || 'ค้นหาเลขที่ใบงาน, รายละเอียด...', label: 'ค้นหา:' }"
      :table-filter-value="searchKeyword"
      @update:table-filter-value="onSearchChange"
      hover
      :pagination="false"
      :loading="isLoading"
      :items-per-page-select="{
        label: $t('table.common.itemsPerPage') || 'แสดงต่อหน้า:',
        values: [10, 20, 50],
        external: true
      }"
      :items-per-page="pageSize"
      @pagination-change="onPageSizeChange"
      responsive
    >
      
      <template #ticketNumber="{ item }">
        <td>
          <div class="font-weight-bold text-primary">{{ item.ticketNumber || '-' }}</div>
        </td>
      </template>

      <template #reported_datetime="{ item }">
        <td>
          <div class="table-date">{{ formatDate(item.userPart.dateTimeInformed) }}</div>
        </td>
      </template>

      <template #issue_user="{ item }">
        <td>
          <div class="font-weight-bold table-user-name">
            {{ getUserName(item) }}
            <span v-if="item.userPart && item.userPart.onBehalfOf" class="text-danger small">
              ({{ $t('table.admin.onBehalf') }} {{ item.userPart.onBehalfOf }})
            </span>
          </div>
          <div class="small text-muted table-user-detail">
            {{ getCategoryName(item) }} | {{ getSubCategoryName(item) }}
          </div>
          <span v-if="item._role === 'reporter'" class="role-badge badge-reporter">{{ $t('table.supervisor.badgeReporter') }}</span>
          <span v-else class="role-badge badge-dept">{{ $t('table.supervisor.badgeDept') }}</span>
          <span v-if="item.rejectionPart && item.rejectionPart.reason && item.status === 'pending'" class="role-badge badge-rejected ml-1" :title="item.rejectionPart.reason">
            {{ $t('ticketDetail.tech.rejectedBadge') }}
          </span>
        </td>
      </template>

      <template #status="{ item }">
        <td class="text-center">
          <span
            class="status-circle"
            :style="{ backgroundColor: getBadge(item.status) }"
            :title="getStatusText(item.status)"
          ></span>
        </td>
      </template>

      <template #show_details="{ item }">
        <td class="text-center">
          <button
            class="circle-action-btn"
            :class="getActionClass(item.status)"
            @click="showDetail(item, false)"
            :title="getActionTitle(item.status)"
          >
            <CIcon :name="getActionIcon(item.status)" size="sm" />
          </button>
        </td>
      </template>
    </CDataTable>

    <CPagination
      v-show="totalPages > 1"
      :pages="totalPages"
      :active-page.sync="currentPage"
      :double-arrows="false"
      align="center"
    />

    <div class="status-legend mt-3">
      <div class="legend-item">
        <span class="status-circle" style="background:#e55353"></span>
        <span>{{ $t('table.admin.legend1') }}</span>
      </div>
      <div class="legend-item">
        <span class="status-circle" style="background:#fd7e14"></span>
        <span>{{ $t('table.admin.legend2') }}</span>
      </div>
      <div class="legend-item">
        <span class="status-circle" style="background:#ffeb3b"></span>
        <span>{{ $t('table.admin.legend3') }}</span>
      </div>
      <div class="legend-item">
        <span class="status-circle" style="background:#2eb85c"></span>
        <span>{{ $t('table.admin.legend4') }}</span>
      </div>
      <div class="legend-item">
        <span class="status-circle" style="background:#cdcdcd"></span>
        <span>{{ $t('table.admin.legend5') }}</span>
      </div>
    </div>
  </CCardBody>
</template>

<style scoped>
/* สไตล์เดิม (คงเดิม) */
.custom-pagination .page-item.active .page-link {
  background-color: #2ba9a5 !important;
  border-color: #2ba9a5 !important;
  color: #ffffff;
}
.custom-pagination .page-item .page-link {
  color: #414141;
}
.custom-pagination .page-item:not(.active) .page-link:hover {
  background-color: #f0f0f0;
}

.circle-action-btn {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid;
}

/* สีตามสถานะ */
.circle-action-btn.primary {
  color: #321fdb;
  border-color: #321fdb;
}
.circle-action-btn.primary:hover {
  background-color: #321fdb;
  color: #ffffff;
}
.circle-action-btn.warning {
  color: #f9b115;
  border-color: #f9b115;
}
.circle-action-btn.warning:hover {
  background-color: #f9b115;
  color: #ffffff;
}

.status-circle {
  display: inline-block;
  width: 30px;
  height: 30px;
  border-radius: 50%;
}

.status-legend {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  align-items: center;
}

.status-legend .status-circle {
  width: 14px;
  height: 14px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #414141;
}

/* Media Query สำหรับมือถือ */
@media (max-width: 768px) {
  .table-date, .table-user-name {
    font-size: 0.9rem;
    white-space: nowrap;
  }
  
  .table-user-detail {
    font-size: 0.75rem;
    white-space: normal;
    min-width: 150px;
  }

  .status-circle {
    width: 24px;
    height: 24px;
  }

  .circle-action-btn {
    width: 30px;
    height: 30px;
  }

  .status-legend {
    gap: 12px;
  }
  .legend-item {
    font-size: 12px;
  }
}
.role-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  margin-top: 3px;
}
.badge-reporter {
  background-color: #e3f2fd;
  color: #1565c0;
  border: 1px solid #90caf9;
}
.badge-dept {
  background-color: #f3e5f5;
  color: #6a1b9a;
  border: 1px solid #ce93d8;
}
.badge-rejected {
  background-color: #ffebee;
  color: #c62828;
  border: 1px solid #ef9a9a;
}
</style>

<script>
import { mapGetters } from "vuex";

export default {
  name: "Sreportable",
  data() {
    const saved = Number(localStorage.getItem('smartfix_page_size'));
    const initialPageSize = [10, 20, 50].includes(saved) ? saved : 50;
    return {
      ticketsData: [],
      isLoading: false,
      currentPage: 1,
      pageSize: initialPageSize,
      totalItems: 0,
      searchKeyword: "",
      searchTimeout: null,
      isMounted: false,
    };
  },
  computed: {
    ...mapGetters("auth", ["profile"]),
    tableFields() {
      return [
        { key: "ticketNumber", label: this.$t("table.user.col.ticketNo"), _style: "width:15%; min-width: 120px;" },
        { key: "reported_datetime", label: this.$t("table.user.col.reportedAt"), _style: "width:15%; min-width: 110px;" },
        { key: "issue_user", label: this.$t("table.supervisor.col.reporterDetail"), _style: "width:40%; min-width: 200px;" },
        {
          key: "status",
          label: this.$t("table.user.col.status"),
          _style: "width:15%; text-align:center; min-width: 80px;",
          _classes: "text-center",
        },
        { key: "show_details", label: this.$t("table.admin.col.actions"), _style: "width:15%; text-align:center; min-width: 80px;" },
      ];
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.pageSize) || 1;
    },
  },
  watch: {
    currentPage() {
      if (this.isMounted) this.loadTickets();
    },
    profile(val) {
      if (val && this.isMounted) this.loadTickets();
    }
  },
  mounted() {
    this.isMounted = true;
    if (this.profile) {
      this.loadTickets();
    }
  },
  methods: {
    onPageSizeChange(newSize) {
      const size = Number(newSize);
      if ([10, 20, 50].includes(size) && size !== this.pageSize) {
        this.pageSize = size;
        localStorage.setItem('smartfix_page_size', String(size));
        this.currentPage = 1;
        this.loadTickets();
      }
    },

    onSearchChange(val) {
      this.searchKeyword = val || "";
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadTickets();
      }, 350);
    },

    async loadTickets() {
      try {
        if (!this.profile) return;

        let departmentId = null;
        if (this.profile.category) {
            departmentId = this.profile.category._id || this.profile.category;
        } else if (this.profile.department) {
            departmentId = this.profile.department._id || this.profile.department;
        }

        if (!departmentId) return;

        this.isLoading = true;
        const supervisorId = this.profile._id;

        const params = new URLSearchParams({
          page: this.currentPage,
          limit: this.pageSize,
        });
        if (supervisorId) {
          params.append('supervisorId', supervisorId);
        }
        if (this.searchKeyword && this.searchKeyword.trim()) {
          params.append('search', this.searchKeyword.trim());
        }

        const res = await fetch(`${process.env.VUE_APP_APP_URL}/ticket/supervisor/${departmentId}/active?${params.toString()}`)
          .then(r => r.json());

        if (res.success && Array.isArray(res.data)) {
          this.ticketsData = res.data.map(t => {
            const isReporter = String(t.userPart?.user?._id || t.userPart?.user) === String(supervisorId);
            return {
              ...t,
              _role: isReporter ? 'reporter' : 'dept',
            };
          });
          this.totalItems = res.pagination?.total ?? this.ticketsData.length;
        }
      } catch (error) {
        console.error("Failed to load tickets:", error);
      } finally {
        this.isLoading = false;
      }
    },

    showDetail(item, isEvaluate) {
      this.$emit("show-detail", {
        ...item,
        isEvaluateMode: isEvaluate,
      });
    },

    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${day}/${month}/${year} ${hours}:${minutes}${this.$t('table.user.timeSuffix')}`;
    },

    getUserName(item) {
      if (item.username) return item.username;
      if (item.userPart?.name) return item.userPart.name;
      if (item.userPart?.user?.name) {
        return `${item.userPart.user.name} ${item.userPart.user.surname || ''}`.trim();
      }
      return this.$t("table.supervisor.noUserName");
    },

    getCategoryName(item) {
      return item.userPart?.category?.departmentName || this.$t("table.supervisor.noCategory");
    },

    getSubCategoryName(item) {
      const sub = item.userPart?.subCategory;
      
      if (!sub) return "-";

      if (typeof sub === 'object') {
        return sub.subCategory || sub.name || sub.title || sub.subcategoryName || "-";
      }

      return sub;
    },

    getStatusText(status) {
      const statusMap = {
        pending: this.$t("table.admin.status.pending"),
        assigned: this.$t("table.admin.status.assigned"),
        in_progress: this.$t("table.admin.status.in_progress"),
        done: this.$t("table.admin.status.done"),
        welldone: this.$t("table.admin.status.welldone"),
        cancelled: this.$t("table.admin.status.cancelled"),
      };
      return statusMap[status] || status;
    },

    getBadge(status) {
      const badgeMap = {
        pending: "#e55353",
        assigned: "#fd7e14",
        in_progress: "#ffeb3b",
        done: "#2eb85c",
        welldone: "#2eb85c",
        cancelled: "#cdcdcd",
      };
      return badgeMap[status] || "#f8f9fa";
    },
    getActionIcon(status) {
      if (status === "pending") return "cil-user-follow";     
      return "cil-magnifying-glass";                          
    },
    getActionTitle(status) {
      if (status === "pending") return this.$t("table.supervisor.action.assign");
      return this.$t("table.supervisor.action.track");
    },
    getActionClass(status) {
      if (status === "pending") return "warning";
      return "primary";
    },
  },
};
</script>